import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripeConfig } from '../config/stripe';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Lock, CreditCard, Check, CheckCircle } from 'lucide-react';
import devLog from '../utils/devLog';

// Initialize Stripe
const stripePromise = loadStripe(stripeConfig.publishableKey);

// Initialize Firebase Functions
const functions = getFunctions();
const createSubscription = httpsCallable(functions, 'createSubscription');
const provisionUser = httpsCallable(functions, 'provisionUser');
const validateCoupon = httpsCallable(functions, 'validateCoupon');

// Price IDs
const PRICE_IDS = {
  standard: process.env.REACT_APP_PRICE_STANDARD,
  standard_plus: process.env.REACT_APP_PRICE_STANDARD_PLUS,
  pro: process.env.REACT_APP_PRICE_PRO,
  enterprise: process.env.REACT_APP_PRICE_ENTERPRISE
};

// Plan details - maps to subscription.plan keys used in SubscriptionPlan.js
const PLANS = {
  standard: { name: 'Standard', price: 30, screens: 1, planKey: '1-screen' },
standard_plus: { name: 'Standard Plus', price: 60, screens: 2, planKey: '2-screen' },
pro: { name: 'Pro', price: 99, screens: 5, planKey: '5-screen' },
enterprise: { name: 'Enterprise', price: 149, screens: 15, planKey: '15-screen' }
};

// Stripe Element Styles
const stripeElementStyle = {
  base: {
    fontSize: '16px',
    color: '#1e293b',
    '::placeholder': {
      color: '#94a3b8',
    },
  },
  invalid: {
    color: '#dc2626',
  },
};

// Payment Form Component
function PaymentForm({ selectedPlan, couponCode, couponDiscount, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useAuth();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [focusedElement, setFocusedElement] = useState(null);

  const plan = PLANS[selectedPlan];
  const priceId = PRICE_IDS[selectedPlan];

  // Calculate discounted price for button display
  const displayPrice = couponDiscount?.percentOff > 0
    ? plan.price * (1 - couponDiscount.percentOff / 100)
    : plan.price;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;

    setProcessing(true);
    setError('');

    try {
      // Create payment method
      const cardNumber = elements.getElement(CardNumberElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
        billing_details: {
          email: currentUser.email,
          name: currentUser.displayName || '',
          address: {
            postal_code: zipCode
          }
        }
      });

      if (pmError) {
        setError(pmError.message);
        setProcessing(false);
        return;
      }

      // Call createSubscription Cloud Function
      const result = await createSubscription({
        priceId: priceId,
        paymentMethodId: paymentMethod.id,
        couponCode: couponCode || null,
        userId: currentUser.uid,
        email: currentUser.email,
        name: currentUser.displayName || ''
      });

      const data = result.data;

      if (!data.success) {
        setError(data.error || 'Failed to create subscription');
        setProcessing(false);
        return;
      }

      // Confirm the payment if clientSecret is returned
      if (data.clientSecret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: paymentMethod.id,
        });
        
        if (confirmError) {
          setError(confirmError.message);
          setProcessing(false);
          return;
        }

        // Check if payment succeeded
        if (paymentIntent.status !== 'succeeded') {
          setError('Payment was not completed. Please try again.');
          setProcessing(false);
          return;
        }
      }

      // Save card details and subscription to Firestore
      // FIXED: Use data.discount from Cloud Function instead of hardcoded values
      const cardData = {
        brand: paymentMethod.card.brand,
        expMonth: paymentMethod.card.exp_month,
        expYear: paymentMethod.card.exp_year,
        last4: paymentMethod.card.last4,
        paymentMethodId: paymentMethod.id
      };

      // Calculate next billing date (1 month from now)
      const nextBillingDate = new Date();
      nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);

      // FIXED: Get discount from Stripe response, not hardcoded
      const discountCode = data.discount?.code || null;
      const discountPercent = data.discount?.percentOff || 0;

      await updateDoc(doc(db, 'users', currentUser.uid), {
        savedCard: cardData,
        billingZip: zipCode,
        subscription: {
          plan: plan.planKey, // '1-screen' or '2-screen'
          screensAllowed: plan.screens,
          screensUsed: 0,
          pricePerMonth: plan.price,
          discountCode: discountCode,        // FIXED: From Stripe
          discountPercent: discountPercent,  // FIXED: From Stripe
          status: 'active',
          nextBillingDate: nextBillingDate.getTime(),
          subscriptionId: data.subscriptionId,
          updatedAt: new Date()
        },
        subscriptionStatus: 'active',
        updatedAt: new Date()
      });

      // Provision user with cloned layouts and playlists
      const planType = selectedPlan;
      devLog('Provisioning user with plan:', planType);
      
      const provisionResult = await provisionUser({
        userId: currentUser.uid,
        plan: planType
      });

      if (!provisionResult.data.success) {
        console.error('Provisioning failed:', provisionResult.data.error);
        // Don't fail the whole flow, just log it
      } else {
        devLog('Provisioning successful:', provisionResult.data);
      }

      // Success - trigger callback with discount info for success screen
      onSuccess({
        plan,
        discount: data.discount
      });

    } catch (err) {
      console.error('Payment error:', err);
      setError('An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Card Number */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: 500,
          color: '#374151',
          marginBottom: '8px'
        }}>
          Card Number
        </label>
        <div style={{
          padding: '14px 16px',
          border: focusedElement === 'cardNumber' ? '2px solid #E63946' : '1px solid #e2e8f0',
          borderRadius: '10px',
          background: '#ffffff',
          transition: 'border-color 0.2s'
        }}>
          <CardNumberElement
            options={{ style: stripeElementStyle }}
            onFocus={() => setFocusedElement('cardNumber')}
            onBlur={() => setFocusedElement(null)}
          />
        </div>
      </div>

      {/* Expiry, CVC, Zip Row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '8px'
          }}>
            Expiration
          </label>
          <div style={{
            padding: '14px 16px',
            border: focusedElement === 'cardExpiry' ? '2px solid #E63946' : '1px solid #e2e8f0',
            borderRadius: '10px',
            background: '#ffffff',
            transition: 'border-color 0.2s'
          }}>
            <CardExpiryElement
              options={{ style: stripeElementStyle }}
              onFocus={() => setFocusedElement('cardExpiry')}
              onBlur={() => setFocusedElement(null)}
            />
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '8px'
          }}>
            CVC
          </label>
          <div style={{
            padding: '14px 16px',
            border: focusedElement === 'cardCvc' ? '2px solid #E63946' : '1px solid #e2e8f0',
            borderRadius: '10px',
            background: '#ffffff',
            transition: 'border-color 0.2s'
          }}>
            <CardCvcElement
              options={{ style: stripeElementStyle }}
              onFocus={() => setFocusedElement('cardCvc')}
              onBlur={() => setFocusedElement(null)}
            />
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '8px'
          }}>
            ZIP Code
          </label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="12345"
            maxLength={10}
            required
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '16px',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#E63946';
              e.target.style.borderWidth = '2px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.borderWidth = '1px';
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{
          width: '100%',
          padding: '16px 24px',
          fontSize: '16px',
          fontWeight: 600,
          color: '#ffffff',
          background: processing ? '#f87171' : '#E63946',
          border: 'none',
          borderRadius: '10px',
          cursor: processing ? 'default' : 'pointer',
          transition: 'background 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        {processing ? (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" opacity="0.3"/>
              <path d="M12 2 A10 10 0 0 1 22 12" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
            </svg>
            Processing...
          </>
        ) : (
          <>
            <Lock size={18} />
            Pay ${displayPrice % 1 === 0 ? displayPrice.toFixed(0) : displayPrice.toFixed(2)}/month
          </>
        )}
      </button>

      {/* Security Note */}
      <p style={{
        marginTop: '16px',
        textAlign: 'center',
        fontSize: '13px',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
      }}>
        <Lock size={14} />
        Secured by Stripe. Your card info is encrypted.
      </p>
    </form>
  );
}

// Success Screen Component - FIXED: Now receives and displays discount
function SuccessScreen({ plan, discount, onContinue }) {
  // Calculate discounted price if discount exists
  const hasDiscount = discount && discount.percentOff > 0;
  const discountedPrice = hasDiscount 
    ? plan.price * (1 - discount.percentOff / 100) 
    : plan.price;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        padding: '48px',
        textAlign: 'center',
        maxWidth: '480px',
        width: '100%',
        margin: '20px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
      }}>
        {/* Success Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          background: '#ecfdf5',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <CheckCircle size={40} color="#10b981" />
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: '#1e293b',
          margin: '0 0 12px 0'
        }}>
          Welcome to Pulse!
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: '16px',
          color: '#64748b',
          margin: '0 0 32px 0',
          lineHeight: 1.6
        }}>
          Your subscription is now active. You're all set to start managing your gym's visual programming.
        </p>

        {/* Plan Summary */}
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{
                fontSize: '14px',
                color: '#64748b',
                marginBottom: '4px'
              }}>
                Your Plan
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#1e293b'
              }}>
                {plan.name}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {hasDiscount ? (
                <>
                  <div style={{
                    fontSize: '14px',
                    color: '#64748b',
                    textDecoration: 'line-through'
                  }}>
                    ${plan.price}/mo
                  </div>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#10b981'
                  }}>
                    ${discountedPrice.toFixed(0)}<span style={{ fontSize: '14px', fontWeight: 400 }}>/mo</span>
                  </div>
                </>
              ) : (
                <div style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#E63946'
                }}>
                  ${plan.price}<span style={{ fontSize: '14px', fontWeight: 400 }}>/mo</span>
                </div>
              )}
            </div>
          </div>

          {/* Discount Applied */}
          {hasDiscount && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              background: '#f0fdf4',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Check size={16} color="#10b981" />
              <span style={{ fontSize: '14px', color: '#166534' }}>
                {discount.percentOff}% discount applied ({discount.code})
              </span>
            </div>
          )}
        </div>

        {/* Continue Button */}
        <button
          onClick={onContinue}
          style={{
            width: '100%',
            padding: '16px 24px',
            fontSize: '16px',
            fontWeight: 600,
            color: '#ffffff',
            background: '#E63946',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

// Main Payment Page Component
function Payment() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null); // FIXED: Store success data including discount

  useEffect(() => {
    // Get plan selection from session storage
    const plan = sessionStorage.getItem('selectedPlan');
    const coupon = sessionStorage.getItem('couponCode');

    if (!plan) {
      // No plan selected, redirect back
      navigate('/select-plan');
      return;
    }

    setSelectedPlan(plan);
    setCouponCode(coupon);

    // Validate coupon immediately if one exists
    if (coupon) {
      validateCoupon({ couponCode: coupon })
        .then(result => {
          if (result.data?.success && result.data?.percentOff > 0) {
            setCouponDiscount({ percentOff: result.data.percentOff, code: coupon });
          }
        })
        .catch(() => {
          // Silently fail - coupon will still be validated on submit
        });
    }
  }, [navigate]);

  // FIXED: handleSuccess now receives data including discount
  const handleSuccess = (data) => {
    // Clear session storage
    sessionStorage.removeItem('selectedPlan');
    sessionStorage.removeItem('couponCode');
    // Store success data and show success screen
    setSuccessData(data);
    setShowSuccess(true);
  };

  const handleContinue = () => {
    navigate('/');
  };

  // Wait for auth and plan
  if (!currentUser || !selectedPlan) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc'
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  const plan = PLANS[selectedPlan];

  // Show success screen after payment - FIXED: Pass discount to success screen
  if (showSuccess && successData) {
    return (
      <SuccessScreen 
        plan={successData.plan} 
        discount={successData.discount}
        onContinue={handleContinue} 
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        padding: '20px 40px'
      }}>
        <img 
          src="/pulse_blue.png" 
          alt="Pulse" 
          style={{ height: '36px' }}
        />
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '960px',
        margin: '0 auto',
        padding: '40px',
        display: 'grid',
        gridTemplateColumns: '1fr 400px',
        gap: '48px'
      }}>
        {/* Left - Order Summary */}
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#1e293b',
            margin: '0 0 32px 0'
          }}>
            Complete your order
          </h1>

          {/* Plan Summary Card */}
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #e2e8f0',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px'
            }}>
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>
                  {plan.name}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#64748b',
                  margin: 0
                }}>
                  {plan.screens} {plan.screens === 1 ? 'screen' : 'screens'} • Billed monthly
                </p>
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#1e293b'
              }}>
                ${plan.price}<span style={{ fontSize: '14px', fontWeight: 400, color: '#64748b' }}>/mo</span>
              </div>
            </div>

            {/* Features */}
            <div style={{
              borderTop: '1px solid #f1f5f9',
              paddingTop: '16px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  `${plan.screens === 1 ? '6' : '5'} workout zones${plan.screens === 2 ? ' per screen' : ''}`,
                  'Full video library',
                  'Request new exercises'
                ].map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#475569'
                  }}>
                    <Check size={16} color="#10b981" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Coupon Applied */}
            {couponCode && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f0fdf4',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Check size={16} color="#10b981" />
                <span style={{ fontSize: '14px', color: '#166534' }}>
                  {couponDiscount
                    ? <>Coupon <strong>{couponCode}</strong>: {couponDiscount.percentOff}% off → <strong>${(plan.price * (1 - couponDiscount.percentOff / 100)).toFixed(0)}/mo</strong></>
                    : <>Coupon <strong>{couponCode}</strong> will be applied</>
                  }
                </span>
              </div>
            )}
          </div>

          {/* Change Plan Link */}
          <button
            onClick={() => navigate('/select-plan')}
            style={{
              background: 'none',
              border: 'none',
              color: '#E63946',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              padding: 0
            }}
          >
            ← Change plan
          </button>
        </div>

        {/* Right - Payment Form */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '32px',
          border: '1px solid #e2e8f0',
          height: 'fit-content'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: '#fef2f2',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CreditCard size={20} color="#E63946" />
            </div>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1e293b',
              margin: 0
            }}>
              Payment Details
            </h2>
          </div>

          <Elements stripe={stripePromise}>
            <PaymentForm 
              selectedPlan={selectedPlan} 
              couponCode={couponCode}
              couponDiscount={couponDiscount}
              onSuccess={handleSuccess}
            />
          </Elements>
        </div>
      </div>

      {/* CSS for spinner */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          @media (max-width: 800px) {
            .payment-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Payment;